import React, { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_SILENCE_MS,
  endsWithTrailingConjunction,
  getMaxSilenceExtensionMs,
  EARLY_ANSWER_WORD_LIMIT,
  getEarlyAnswerSilenceMs,
} from '../../utils/turnDetection';
import { getPublicApiOrigin } from '../../config/env';

const SILENCE_CHECK_INTERVAL_MS = 150;
// Acoustic Speech Verification: Minimum RMS audio energy to consider active foreground voice.
// Ambient office room baseline with WebRTC noise cancellation is typically < 0.015.
// Candidate speaking directly into laptop/headset mic produces 0.04 to 0.25+.
const VOICE_ACTIVITY_RMS_THRESHOLD = 0.025;
// Gate 2 (realtime-stt-benchmark.html) confirmed live: gpt-live-transcribe
// requires turn_detection: null, and with it null NOTHING auto-commits —
// conversation.item.input_audio_transcription.completed never fires on its
// own (130 deltas, 0 completed in that run). Per OpenAI's realtime-transcription
// / realtime-conversations guides: "with automatic turn detection turned off,
// commit the buffer when you want to finish an audio turn" via an explicit
// input_audio_buffer.commit — same event, WebSocket or WebRTC data channel,
// no separate mechanism documented for either transport. sendCommit() below
// is that explicit call, fired the moment our own silence-clock (or a manual
// stop) decides the turn is over — mirroring VoiceRecorder's real
// awaitingFinalizeRef + recognition.onend pattern instead of blindly
// draining for a fixed window (this used to be FINALIZE_DRAIN_MS, a
// documented-as-unvalidated placeholder — now FINALIZE_TIMEOUT_MS is a true
// fallback for a commit that never answers, not the only mechanism).
const FINALIZE_TIMEOUT_MS = 600;
const REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

/**
 * OpenAI Realtime transcription over WebRTC — mounted by
 * CandidateInterviewRoom.jsx as the default candidate-facing recorder
 * (STT_PROVIDER=openai_realtime in config.py).
 *
 * Validation status as of this wiring: sample-answer transcripts (short/
 * medium/long, synthetic TTS audio piped through the same explicit-commit
 * path sendCommit() uses) came back clean. NOT yet validated:
 *   1. Worst-case transcripts (e.g. the "re-describe"/"read describe"
 *      mishearing from session be9b071f) have not been replayed through
 *      this and compared against Web Speech API's output.
 *   2. Real interim-delta frequency/timing has not been benchmarked
 *      against the 750ms activity-ping throttle (CandidateInterviewRoom.jsx)
 *      and the 10s/60s silence thresholds (websocket_session.py) against a
 *      real microphone — only synthetic TTS audio has been run through it
 *      so far. OpenAI's deltas are coarser-grained than Web Speech API's
 *      interim results (see the .delta case below) and may arrive on a
 *      different cadence.
 * Kept API-shape-identical to VoiceRecorder.jsx (isRecording/onStart/
 * onStop/onTranscript/onAudioBlob/onSilenceDetected/silenceThresholdMs/
 * disabled/interviewCompleted/showTranscript/language/aiSpeaking/
 * lastSpokenText/micEnabled) — including VoiceRecorder's lastSpokenText
 * prop, reusing the same looksLikeAiEcho() check from VoiceRecorder.jsx
 * (imported, not reimplemented) at the same fold point: buffered speech
 * captured while aiSpeaking was true is checked against everything the AI
 * has said before being folded into the answer on the aiSpeaking->false
 * transition, and discarded instead of folded if it looks like AI-speaker
 * leakage rather than genuine early-start candidate speech. sessionId/
 * accessToken are the other additions — needed to mint the realtime client
 * secret server-side (app/api/realtime_stt.py).
 *
 * Deliberately does NOT replace the app-level silence clock: OpenAI's
 * server_vad only controls audio chunking inside the transcription
 * session (see mint_transcription_client_secret), it has no notion of
 * ASKING/LISTENING/nudge/move-on. onSilenceDetected below is the same
 * local JS timer VoiceRecorder uses, unchanged.
 */
export const OpenAIRealtimeRecorder = ({
  isRecording,
  onStart,
  onStop,
  onTranscript,
  onAudioBlob,
  onSilenceDetected,
  silenceThresholdMs = DEFAULT_SILENCE_MS,
  disabled,
  interviewCompleted,
  showTranscript = false,
  language = 'en',
  aiSpeaking = false,
  // Every AI clip's text spoken so far this interview (see
  // CandidateInterviewRoom.jsx's lastSpokenTextRef) — compared against a
  // buffered aiSpeaking-window fragment at fold time via looksLikeAiEcho()
  // to tell a genuine early-start answer apart from the AI's own speaker
  // audio leaking into the mic. Optional: absent (or stale), the fold
  // behaves exactly as before this check existed. Same contract as
  // VoiceRecorder.jsx's identically-named prop.
  lastSpokenText = '',
  micEnabled = true,
  // Scaffold-only additions beyond VoiceRecorder's contract — needed to
  // mint the ephemeral realtime token server-side (see
  // app/api/realtime_stt.py). CandidateInterviewRoom already has both
  // available (session_id from the URL, access token from the same place
  // it hands the websocket its token) whenever this eventually gets wired
  // in for real.
  sessionId,
  accessToken,
  onError,
}) => {
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');

  const pcRef = useRef(null);
  const dataChannelRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioSourceRef = useRef(null);
  const zeroGainRef = useRef(null);
  const clonedTrackRef = useRef(null);
  const isConnectedRef = useRef(false);
  const isConnectingRef = useRef(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const fullTranscriptRef = useRef('');
  const pendingInterimRef = useRef('');
  const preAnswerBufferRef = useRef('');
  const aiSpeakingRef = useRef(aiSpeaking);
  const lastSpokenTextRef = useRef(lastSpokenText);
  const isRecordingRef = useRef(isRecording);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  const micEnabledRef = useRef(micEnabled);
  const interviewCompletedRef = useRef(interviewCompleted);
  const isStoppingRef = useRef(false);

  const onStartRef = useRef(onStart);
  const onStopRef = useRef(onStop);
  const onTranscriptRef = useRef(onTranscript);
  const onAudioBlobRef = useRef(onAudioBlob);
  const onSilenceDetectedRef = useRef(onSilenceDetected);
  const silenceThresholdMsRef = useRef(silenceThresholdMs);

  useEffect(() => { aiSpeakingRef.current = aiSpeaking; }, [aiSpeaking]);
  useEffect(() => { lastSpokenTextRef.current = lastSpokenText; }, [lastSpokenText]);
  useEffect(() => { micEnabledRef.current = micEnabled; }, [micEnabled]);
  useEffect(() => { interviewCompletedRef.current = interviewCompleted; }, [interviewCompleted]);
  useEffect(() => { onStartRef.current = onStart; }, [onStart]);
  useEffect(() => { onStopRef.current = onStop; }, [onStop]);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onAudioBlobRef.current = onAudioBlob; }, [onAudioBlob]);
  useEffect(() => { onSilenceDetectedRef.current = onSilenceDetected; }, [onSilenceDetected]);
  useEffect(() => { silenceThresholdMsRef.current = silenceThresholdMs; }, [silenceThresholdMs]);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const silenceCheckRef = useRef(null);
  const lastActivityRef = useRef(0);
  const lastSttBatchTimeRef = useRef(0);
  const isSpeakingRef = useRef(false);
  const silenceFiredRef = useRef(false);

  // True from the moment we've sent input_audio_buffer.commit for the turn
  // that just ended until either the matching `completed` event lands or
  // FINALIZE_TIMEOUT_MS elapses — same role as VoiceRecorder's
  // awaitingFinalizeRef, just resolved by an explicit commit response
  // instead of recognition.onend.
  const awaitingFinalizeRef = useRef(false);
  const finalizeTimeoutRef = useRef(null);

  const sendDataChannelEvent = (payload) => {
    const dc = dataChannelRef.current;
    if (!dc || dc.readyState !== 'open') return false;
    dc.send(JSON.stringify(payload));
    return true;
  };

  const sendCommit = () => sendDataChannelEvent({ type: 'input_audio_buffer.commit' });

  // Defensive: docs don't say whether the server-side input buffer is
  // cleared after a commit or keeps accumulating for the next turn. Sent
  // before a NEW turn's audio starts flowing so a "yes it keeps
  // accumulating" answer can't bleed prior (already-committed, or
  // aiSpeaking-window) audio into this turn's transcript.
  const sendClear = () => sendDataChannelEvent({ type: 'input_audio_buffer.clear' });

  // Samples the current RMS audio energy of the candidate's microphone.
  // Used by the acoustic verification gate to detect whether the candidate is
  // actively speaking into the mic when the silence threshold elapses.
  const getAudioEnergyRms = () => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    try {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
      const buffer = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(buffer);
      let sumSquares = 0;
      for (let i = 0; i < buffer.length; i++) {
        const normalized = (buffer[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      return Math.sqrt(sumSquares / buffer.length);
    } catch (_) {
      return 0;
    }
  };

  const finalizeAndReportStop = () => {
    if (finalizeTimeoutRef.current) {
      clearTimeout(finalizeTimeoutRef.current);
      finalizeTimeoutRef.current = null;
    }
    awaitingFinalizeRef.current = false;
    const finalText = (fullTranscriptRef.current + ' ' + pendingInterimRef.current).trim();
    pendingInterimRef.current = '';
    if (onStopRef.current) onStopRef.current(finalText);
  };

  // ============ REALTIME EVENT HANDLING ============
  const handleRealtimeEvent = (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      console.warn('⚠️ Non-JSON realtime event:', event.data);
      return;
    }

    switch (data.type) {
      case 'conversation.item.input_audio_transcription.delta': {
        if (aiSpeakingRef.current) break;
        lastActivityRef.current = Date.now();
        lastSttBatchTimeRef.current = Date.now();
        pendingInterimRef.current += data.delta || '';
        setInterimTranscript(pendingInterimRef.current);
        if (onTranscriptRef.current && !interviewCompletedRef.current) {
          onTranscriptRef.current(pendingInterimRef.current, false);
        }
        break;
      }
      case 'conversation.item.input_audio_transcription.completed': {
        const finalText = (data.transcript || '').trim();
        pendingInterimRef.current = '';
        setInterimTranscript('');
        if (finalText) {
          lastActivityRef.current = Date.now();
          lastSttBatchTimeRef.current = Date.now();
          if (aiSpeakingRef.current) {
            console.log(`✋ Realtime STT: Discarding transcript while AI speaking: "${finalText}"`);
          } else {
            fullTranscriptRef.current += finalText + ' ';
            setFinalTranscript(fullTranscriptRef.current);
            if (onTranscriptRef.current && !interviewCompletedRef.current) {
              onTranscriptRef.current(finalText, true);
            }
          }
        }
        // Fast path: the commit we sent on stop actually resolved — report
        // now instead of waiting out the fallback timeout.
        if (awaitingFinalizeRef.current) {
          finalizeAndReportStop();
        }
        break;
      }
      case 'input_audio_buffer.speech_started': {
        isSpeakingRef.current = true;
        lastActivityRef.current = Date.now();
        break;
      }
      case 'input_audio_buffer.speech_stopped': {
        isSpeakingRef.current = false;
        lastActivityRef.current = Date.now();
        break;
      }
      case 'error':
        console.error('❌ OpenAI Realtime error event:', data);
        // A turn ending on ~0 spoken audio (e.g. a stray silence-fire right
        // after Start, before any speech) hits this — empirically confirmed
        // via Gate 1, not documented by OpenAI. Not a real failure: there
        // is definitively no `completed` coming for this commit, so finalize
        // now with whatever text we already have (almost certainly none)
        // instead of waiting out the fallback timeout for no reason.
        if (awaitingFinalizeRef.current && data.error?.code === 'input_audio_buffer_commit_empty') {
          finalizeAndReportStop();
        }
        break;
      default:
        break;
    }
  };

  // ============ CONNECTION LIFECYCLE ============
  const connect = async () => {
    if (isConnectingRef.current || isConnectedRef.current) return;
    if (!sessionId || !accessToken) {
      console.error('❌ OpenAIRealtimeRecorder: sessionId/accessToken required to mint a realtime token');
      if (onErrorRef.current) onErrorRef.current(new Error('Missing sessionId or accessToken'));
      return;
    }
    isConnectingRef.current = true;
    try {
      const tokenResp = await fetch(
        `${getPublicApiOrigin()}/api/v1/sessions/${sessionId}/realtime-stt-token?token=${encodeURIComponent(accessToken)}&language=${encodeURIComponent(language)}`,
        { method: 'POST' },
      );
      if (!tokenResp.ok) throw new Error(`Token mint failed: ${tokenResp.status}`);
      const tokenData = await tokenResp.json();
      const ephemeralKey = tokenData.value || tokenData?.client_secret?.value;
      if (!ephemeralKey) throw new Error('No client secret in token response');

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') {
          console.error('❌ OpenAI Realtime WebRTC connection failed');
          disconnect();
          if (onErrorRef.current) onErrorRef.current(new Error('WebRTC connection failed'));
        }
      };

      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;
      dc.onmessage = handleRealtimeEvent;
      dc.onopen = () => {
        console.log('✅ OpenAI Realtime data channel open');
        isConnectedRef.current = true;
      };
      dc.onclose = () => {
        console.log('🛑 OpenAI Realtime data channel closed');
        isConnectedRef.current = false;
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          googEchoCancellation: { ideal: true },
          googAutoGainControl: { ideal: true },
          googNoiseSuppression: { ideal: true },
          googHighpassFilter: { ideal: true },
        },
      });
      micStreamRef.current = stream;

      // 1. Send the untouched raw microphone stream directly to WebRTC
      stream.getAudioTracks().forEach((t) => { t.enabled = false; });
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      // 2. Isolate Web Audio by cloning the audio track for acoustic energy monitoring
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const rawAudioTrack = stream.getAudioTracks()[0];
        if (AudioCtx && rawAudioTrack) {
          const clonedTrack = rawAudioTrack.clone();
          clonedTrackRef.current = clonedTrack;
          const monitorStream = new MediaStream([clonedTrack]);

          const audioCtx = new AudioCtx();
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 512;
          analyser.smoothingTimeConstant = 0.2;

          const source = audioCtx.createMediaStreamSource(monitorStream);
          const zeroGain = audioCtx.createGain();
          zeroGain.gain.value = 0; // Muted dummy sink: prevents feedback/echo while keeping audio clock running

          source.connect(analyser);
          analyser.connect(zeroGain);
          zeroGain.connect(audioCtx.destination);

          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
          audioSourceRef.current = source;
          zeroGainRef.current = zeroGain;

          if (audioCtx.state === 'suspended') {
            audioCtx.resume().catch(() => {});
          }
          console.log('✅ Web Audio Analyser initialized on isolated cloned track with zero-gain sink');
        }
      } catch (err) {
        console.warn('⚠️ Web Audio Analyser setup failed (non-blocking):', err);
      }

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResp = await fetch(REALTIME_CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      });
      if (!sdpResp.ok) throw new Error(`SDP exchange failed: ${sdpResp.status}`);
      const answerSdp = await sdpResp.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      // Keep microphone track state synchronized with current recording turn.
      // Initially muted during Question 1 audio/David's speech, enabled when candidate's turn starts.
      stream.getAudioTracks().forEach((t) => {
        t.enabled = isRecordingRef.current && micEnabledRef.current;
      });

      console.log('✅ OpenAI Realtime WebRTC connected');
    } catch (e) {
      console.error('❌ OpenAI Realtime connect failed:', e);
      disconnect();
      if (onErrorRef.current) {
        onErrorRef.current(e);
      }
    } finally {
      isConnectingRef.current = false;
    }
  };

  const disconnect = () => {
    isConnectedRef.current = false;
    if (dataChannelRef.current) {
      try { dataChannelRef.current.close(); } catch (e) { /* ignore */ }
      dataChannelRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (e) { /* ignore */ }
      pcRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (clonedTrackRef.current) {
      try { clonedTrackRef.current.stop(); } catch (_) {}
      clonedTrackRef.current = null;
    }
    if (audioSourceRef.current) {
      try { audioSourceRef.current.disconnect(); } catch (_) {}
      audioSourceRef.current = null;
    }
    if (zeroGainRef.current) {
      try { zeroGainRef.current.disconnect(); } catch (_) {}
      zeroGainRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try { audioContextRef.current.close(); } catch (_) {}
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
      mediaRecorderRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      isStoppingRef.current = true;
      if (silenceCheckRef.current) {
        clearInterval(silenceCheckRef.current);
        silenceCheckRef.current = null;
      }
      // A pending finalize must never fire after unmount (e.g. a commit's
      // `completed`/error response, or the fallback timeout, landing just
      // after teardown) — same guard VoiceRecorder.jsx applies for its own
      // awaitingFinalizeRef.
      awaitingFinalizeRef.current = false;
      if (finalizeTimeoutRef.current) {
        clearTimeout(finalizeTimeoutRef.current);
        finalizeTimeoutRef.current = null;
      }
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- teardown only, no deps needed
  }, []);

  // When interview finishes, immediately disconnect WebRTC and release microphone tracks
  useEffect(() => {
    if (interviewCompleted) {
      disconnect();
    }
  }, [interviewCompleted]);

  // Pre-connect WebRTC on mount with an initially muted audio track (track.enabled = false)
  // so Question 1 has 0ms connection latency when the candidate begins speaking.
  useEffect(() => {
    if (
      !disabled &&
      !interviewCompleted &&
      sessionId &&
      accessToken &&
      !isConnectedRef.current &&
      !isConnectingRef.current
    ) {
      connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on readiness change
  }, [disabled, interviewCompleted, sessionId, accessToken]);

  // ============ TURN START/STOP (mirrors VoiceRecorder) ============
  const prevIsRecordingRef = useRef(false);

  useEffect(() => {
    // Listen strictly when isRecording is true (the candidate's active answering turn).
    // Mic is muted while David is speaking (aiSpeaking) to eliminate echo leakage and phantom speech.
    const shouldListen = isRecording && !interviewCompleted;
    const wasRecordingTurn = prevIsRecordingRef.current;
    prevIsRecordingRef.current = isRecording;

    if (disabled) return undefined;

    if (shouldListen) {
      if (!isConnectedRef.current && !isConnectingRef.current) {
        connect();
      }

      if (isRecording) {
        // Barge-in and AI-speaking buffering unplugged — start every turn with a completely clean slate
        preAnswerBufferRef.current = '';
        fullTranscriptRef.current = '';
        pendingInterimRef.current = '';
        setFinalTranscript('');
        isStoppingRef.current = false;

        // Defensive clear before this turn's audio starts flowing — see
        // sendClear() comment. Fired before the mic track re-enables below,
        // not after, so it can't race newly-arriving audio for this turn.
        sendClear();

        isSpeakingRef.current = false;
        lastActivityRef.current = Date.now();
        lastSttBatchTimeRef.current = 0;
        silenceFiredRef.current = false;
        if (silenceCheckRef.current) clearInterval(silenceCheckRef.current);
        silenceCheckRef.current = setInterval(() => {
          if (silenceFiredRef.current || isStoppingRef.current) return;
          // 1. Continuous Live Voice Activity Keep-Alive:
          // Sample physical microphone RMS energy via Web Audio analyser.
          // While the candidate is speaking into the mic, keep lastActivityRef fresh so that
          // OpenAI Realtime cloud decoder latency (3-4s batching delay) cannot cause premature cutoffs.
          const currentRms = getAudioEnergyRms();
          if (currentRms >= VOICE_ACTIVITY_RMS_THRESHOLD) {
            lastActivityRef.current = Date.now();
            return;
          }

          const spoken = (fullTranscriptRef.current + ' ' + pendingInterimRef.current).trim();
          if (!spoken) return;

          // Dynamic Early-Bird Threshold: 4.5s when <= 10 words (opening formulation).
          // Switches to standard silenceThresholdMsRef (3.5s) once answer is underway (> 10 words).
          const wordCount = spoken.split(/\s+/).filter(Boolean).length;
          const base = wordCount <= EARLY_ANSWER_WORD_LIMIT
            ? getEarlyAnswerSilenceMs()
            : silenceThresholdMsRef.current;
          const threshold = endsWithTrailingConjunction(spoken)
            ? Math.min(base * 2, base + getMaxSilenceExtensionMs())
            : base;

          const elapsed = Date.now() - lastActivityRef.current;

          if (elapsed >= threshold) {
            // 2. OpenAI Realtime STT Batch Empty Check:
            // Ensure no streaming STT transcript delta batch arrived within the recent window
            const sttBatchElapsed = Date.now() - lastSttBatchTimeRef.current;
            if (lastSttBatchTimeRef.current > 0 && sttBatchElapsed < threshold) {
              console.log(`⏳ STT transcript delta received recently (${sttBatchElapsed}ms < ${threshold}ms) — batch active, deferring cutoff`);
              lastActivityRef.current = Date.now();
              return;
            }

            silenceFiredRef.current = true;
            console.log(`🔇 Silence confirmed (${elapsed}ms >= ${threshold}ms, RMS: ${currentRms.toFixed(3)}, STT batch idle) — auto-submitting`);
            if (onSilenceDetectedRef.current) onSilenceDetectedRef.current();
          }
        }, SILENCE_CHECK_INTERVAL_MS);

        audioChunksRef.current = [];
        if (micStreamRef.current) {
          try {
            const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'].find(
              (t) => MediaRecorder.isTypeSupported(t),
            ) || 'audio/webm';
            const recorder = new MediaRecorder(micStreamRef.current, { mimeType });
            recorder.ondataavailable = (e) => {
              if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
              if (audioChunksRef.current.length > 0 && onAudioBlobRef.current) {
                const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
                onAudioBlobRef.current(blob);
              }
            };
            recorder.start(1000);
            mediaRecorderRef.current = recorder;
          } catch (e) {
            console.warn('⚠️ MediaRecorder setup failed (STT only):', e.message);
          }
        }

        if (onStartRef.current) onStartRef.current();
      }

      if (micStreamRef.current) {
        micStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = micEnabledRef.current; });
      }
    } else if (wasRecordingTurn || interviewCompleted) {
      isStoppingRef.current = true;
      if (silenceCheckRef.current) {
        clearInterval(silenceCheckRef.current);
        silenceCheckRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = false; });
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
        mediaRecorderRef.current = null;
      }

      // Explicit finalize: turn_detection is null (required for
      // gpt-live-transcribe), so nothing auto-commits — send commit now,
      // then wait for the real `completed` event (fast path, handled in
      // handleRealtimeEvent) with FINALIZE_TIMEOUT_MS as a fallback for a
      // commit that never answers (dropped data channel message, etc.).
      if (finalizeTimeoutRef.current) {
        clearTimeout(finalizeTimeoutRef.current);
        finalizeTimeoutRef.current = null;
      }
      awaitingFinalizeRef.current = true;
      // Guard: committing an empty/near-silent buffer throws
      // input_audio_buffer_commit_empty (Gate 1, see the 'error' case
      // above) — if nothing has been transcribed for this turn yet, skip
      // the commit round-trip entirely and finalize with what we have.
      const spokenThisTurn = (fullTranscriptRef.current + ' ' + pendingInterimRef.current).trim();
      const words = spokenThisTurn.split(/\s+/).filter(Boolean);
      if (!spokenThisTurn || words.length >= 3) {
        sendCommit();
        finalizeAndReportStop();
      } else {
        const committed = sendCommit();
        if (!committed) {
          // No open data channel to commit on (e.g. already disconnected) —
          // nothing will ever answer this; finalize immediately with
          // whatever text is already in hand instead of waiting out the
          // timeout for no reason.
          finalizeAndReportStop();
        } else {
          finalizeTimeoutRef.current = setTimeout(finalizeAndReportStop, FINALIZE_TIMEOUT_MS);
        }
      }
    } else if (micStreamRef.current) {
      // Not candidate's turn (e.g. David is speaking) — keep track muted to prevent echo or ambient leakage
      micStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = false; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, aiSpeaking, interviewCompleted, disabled]);

  // Soft-mute without ending the WebRTC session — same semantics as
  // VoiceRecorder's mic-toggle effect.
  useEffect(() => {
    if (micStreamRef.current && !interviewCompleted) {
      micStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = isRecordingRef.current && micEnabled;
      });
    }
  }, [micEnabled, interviewCompleted]);

  // ============ RENDER — debug/admin parity with VoiceRecorder ============
  return (
    <div className="voice-recorder" style={{ width: '100%' }}>
      {showTranscript && (finalTranscript || interimTranscript) && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          minHeight: '100px',
          maxHeight: '300px',
          overflowY: 'auto',
          border: '2px solid #e9ecef',
        }}>
          <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '10px', fontWeight: '600' }}>
            📝 Live Transcript (OpenAI Realtime — scaffold):
          </div>
          <div style={{ fontSize: '16px', lineHeight: '1.8' }}>
            <span style={{ color: '#212529' }}>{finalTranscript}</span>
            <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>{interimTranscript}</span>
          </div>
        </div>
      )}
    </div>
  );
};
